import { memo } from 'react';
import type { ScheduleZoomFloors } from '../../../../../features/schedule/scheduleZoomBounds';
import { Schedule } from '../DnDSchedule/Schedule';
import { useScheduleZoom } from './ScheduleZoomContext';
import type { SchedulePlacementController } from './schedulePlacementState';
import {
  focusedPlacementSlotForGrid,
  schedulePlacementPropsAreEqual,
} from './schedulePlacementEquality';
import { ScheduleSelectionMarquee } from './ScheduleSelectionMarquee';
import { useAppSelector } from '../../../../../hooks/redux';
import { MIN_SCHEDULE_BULK_ADJUST } from '../../../../../features/schedule/scheduleSelectionConstants';
import { blurScheduleToolbarFilters } from './scheduleLayoutHooks';
import {
  selectScheduleMutationBlocked,
} from '../../../../../features/schedule/scheduleSelectors';

type ScheduleGridHostProps = {
  selectedDayKey: number;
  placement: SchedulePlacementController;
  onDayZoomFloorsChange: (dayKey: number, floors: ScheduleZoomFloors) => void;
  showScheduleConflicts: boolean;
  conflictMatchIdList: number[];
  blockInvalidPlacement: boolean;
  focusedConflictMatchId: number | null;
  focusedConflictPeerIds: number[];
  conflictFocusPulseGeneration: number;
};

function ScheduleGridHostInner({
  selectedDayKey,
  placement,
  onDayZoomFloorsChange,
  showScheduleConflicts,
  conflictMatchIdList,
  blockInvalidPlacement,
  focusedConflictMatchId,
  focusedConflictPeerIds,
  conflictFocusPulseGeneration,
}: ScheduleGridHostProps) {
  const { scale: scheduleItemScaleMultiplier, columnWidth: scheduleColumnWidth, isGesturing } =
    useScheduleZoom();
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);
  const placeModeActive = placement.pendingPlaceMatchId != null;

  return (
    <main
      ref={placement.scheduleGridWheelRef}
      className={`schedule-workbench__grid${
        placement.shiftModifierHeld ? ' schedule-workbench__grid--shift-marquee' : ''
      }`}
      style={{ minHeight: 0, position: 'relative' }}
      onPointerDownCapture={() => blurScheduleToolbarFilters()}
    >
      <ScheduleSelectionMarquee
        containerRef={placement.scheduleGridWheelRef}
        disabled={scheduleMutationBlocked || placeModeActive || isGesturing}
      />
      <Schedule
        gridDayKey={selectedDayKey}
        scheduleItemScaleMultiplier={scheduleItemScaleMultiplier}
        scheduleColumnWidth={scheduleColumnWidth}
        pendingPlaceMatchId={placement.pendingPlaceMatchId}
        dragModifierBlocked={placement.keyboardModifierHeld}
        shiftModifierHeld={placement.shiftModifierHeld}
        onPlaceAtFreeSlot={placement.handlePlaceAtFreeSlot}
        focusedPlacementSlot={focusedPlacementSlotForGrid(placement)}
        focusedSwapTargetMatchId={placement.focusedSwapTargetMatchId}
        activePlacementMatch={placement.activePlacementMatch}
        onSwapPendingWithScheduled={placement.handleSwapPendingWithScheduled}
        pendingPlacementSource={placement.pendingPlacementSource}
        swapModifierHeld={placement.swapModifierHeld}
        scheduleDragActive={placement.dragPlacement != null}
        dragPlacementSource={placement.dragPlacement?.source ?? null}
        groupDragMatchIds={placement.dragPlacement?.groupMatchIds ?? null}
        groupDragAnchorMatchId={
          placement.dragPlacement?.groupMatchIds &&
          placement.dragPlacement.groupMatchIds.length >= MIN_SCHEDULE_BULK_ADJUST
            ? placement.dragPlacement.match.id
            : null
        }
        onSelectGridMatchForPlacement={placement.handleSelectGridMatchForPlacement}
        placementSessionReady={placement.placementSessionReady}
        placementSessionIndex={placement.placementSessionIndex}
        onZoomFloorsChange={onDayZoomFloorsChange}
        showScheduleConflicts={showScheduleConflicts}
        conflictMatchIdList={conflictMatchIdList}
        blockInvalidPlacement={blockInvalidPlacement}
        focusedConflictMatchId={focusedConflictMatchId}
        focusedConflictPeerIds={focusedConflictPeerIds}
        conflictFocusPulseGeneration={conflictFocusPulseGeneration}
      />
    </main>
  );
}

export const ScheduleGridHost = memo(ScheduleGridHostInner, (prev, next) => {
  if (prev.selectedDayKey !== next.selectedDayKey) return false;
  if (prev.onDayZoomFloorsChange !== next.onDayZoomFloorsChange) return false;
  if (prev.showScheduleConflicts !== next.showScheduleConflicts) return false;
  if (prev.conflictMatchIdList !== next.conflictMatchIdList) return false;
  if (prev.blockInvalidPlacement !== next.blockInvalidPlacement) return false;
  if (prev.focusedConflictMatchId !== next.focusedConflictMatchId) return false;
  if (prev.focusedConflictPeerIds !== next.focusedConflictPeerIds) return false;
  if (prev.conflictFocusPulseGeneration !== next.conflictFocusPulseGeneration) return false;
  return schedulePlacementPropsAreEqual(prev.placement, next.placement);
});
